import { useState, useEffect } from "react";

export default function Status() {
  const [uptime, setUptime] = useState("99.94%");
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const systemStatus = [
    {
      name: "ExamLock Platform",
      status: "operational", 
      description: "Main application and user dashboard",
      uptime: "99.96%",
      responseTime: "245ms",
      lastIncident: "No incidents in the last 90 days",
      icon: "🟢"
    },
    {
      name: "AI Exam Builder",
      status: "operational",
      description: "AI-powered exam creation and question generation", 
      uptime: "99.92%",
      responseTime: "1.2s",
      lastIncident: "Brief slowdown on Aug 15, 2024",
      icon: "🟢"
    },
    {
      name: "Real-time Monitoring",
      status: "operational",
      description: "Live exam monitoring and cheating detection",
      uptime: "99.98%", 
      responseTime: "180ms",
      lastIncident: "No incidents in the last 90 days",
      icon: "🟢"
    },
    {
      name: "Student Portal",
      status: "operational",
      description: "Student exam access and submission system",
      uptime: "99.94%",
      responseTime: "320ms", 
      lastIncident: "Scheduled maintenance on Aug 10, 2024",
      icon: "🟢"
    },
    {
      name: "Authentication Service", 
      status: "operational",
      description: "User login and security systems",
      uptime: "99.99%",
      responseTime: "95ms",
      lastIncident: "No incidents in the last 90 days", 
      icon: "🟢"
    },
    {
      name: "Billing & Payments",
      status: "operational",
      description: "Subscription and payment processing via Stripe",
      uptime: "99.97%",
      responseTime: "156ms",
      lastIncident: "No incidents in the last 90 days",
      icon: "🟢"
    },
    {
      name: "Email Notifications",
      status: "operational", 
      description: "Automated emails and notifications",
      uptime: "99.91%",
      responseTime: "2.1s",
      lastIncident: "Delayed emails on Aug 8, 2024",
      icon: "🟢"
    },
    {
      name: "Data Backup Systems",
      status: "operational",
      description: "Automated backups and data recovery", 
      uptime: "100%",
      responseTime: "N/A",
      lastIncident: "No incidents in the last 90 days",
      icon: "🟢"
    }
  ];

  const recentIncidents = [
    {
      date: "Aug 15, 2024",
      title: "AI Exam Builder Performance",
      description: "Brief slowdown in AI question generation due to high demand",
      severity: "minor",
      duration: "23 minutes",
      status: "resolved",
      icon: "🟡"
    },
    {
      date: "Aug 10, 2024", 
      title: "Scheduled Maintenance",
      description: "Planned system updates and security patches applied",
      severity: "maintenance",
      duration: "2 hours",
      status: "completed",
      icon: "🔵"
    },
    {
      date: "Aug 8, 2024",
      title: "Email Delivery Delays", 
      description: "Some notification emails experienced delivery delays",
      severity: "minor",
      duration: "45 minutes", 
      status: "resolved",
      icon: "🟡"
    }
  ];

  const upcomingMaintenance = [
    {
      date: "Sep 15, 2024",
      time: "2:00 AM - 4:00 AM PST",
      title: "Database Optimization",
      description: "Performance improvements and index optimization",
      expectedDowntime: "30 minutes",
      impact: "Minimal - most features will remain available"
    },
    {
      date: "Sep 22, 2024", 
      time: "1:00 AM - 3:00 AM PST",
      title: "Security Updates",
      description: "Application security patches and dependency updates",
      expectedDowntime: "45 minutes", 
      impact: "Brief interruption during deployment"
    }
  ];

  const globalStats = [
    { label: "Global Uptime", value: "99.94%", icon: "📊" },
    { label: "Exams Delivered Today", value: "2,847", icon: "📝" },
    { label: "Active Users", value: "12,435", icon: "👥" },
    { label: "Data Centers", value: "3", icon: "🏢" }
  ];

  const performanceMetrics = [
    { metric: "API Response Time", value: "245ms", trend: "stable", color: "green" },
    { metric: "Page Load Time", value: "1.2s", trend: "improving", color: "green" },
    { metric: "Database Query Time", value: "45ms", trend: "stable", color: "green" },
    { metric: "Error Rate", value: "0.002%", trend: "improving", color: "green" }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case "operational": return "bg-green-100 text-green-800 border-green-200";
      case "degraded": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "down": return "bg-red-100 text-red-800 border-red-200";
      case "maintenance": return "bg-blue-100 text-blue-800 border-blue-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case "minor": return "bg-yellow-100 text-yellow-800";
      case "major": return "bg-red-100 text-red-800";
      case "maintenance": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              🟢 System Status
            </h1>
            <p className="text-xl text-green-100 mb-8 leading-relaxed">
              Real-time status and performance metrics for all ExamLock services. 
              We're committed to 99.9%+ uptime for your educational needs.
            </p>
            
            <div className="bg-white bg-opacity-20 rounded-2xl p-8 backdrop-blur-sm">
              <div className="text-4xl font-bold text-green-100 mb-2">All Systems Operational</div>
              <div className="text-lg text-green-200">
                Last updated: {currentTime.toLocaleString()} PST
              </div>
              <div className="mt-4 flex items-center justify-center space-x-2">
                <span className="w-3 h-3 bg-green-300 rounded-full animate-pulse"></span>
                <span className="text-green-100">Monitoring {systemStatus.length} services across 3 data centers</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global Stats */}
      <div className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {globalStats.map((stat, index) => (
              <div key={index} className="text-center bg-gray-50 rounded-xl p-6">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">🛡️ Service Status</h2>
              <p className="text-xl text-gray-600">
                Real-time monitoring of all ExamLock components and services
              </p>
            </div>

            <div className="grid gap-6">
              {systemStatus.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-4">{service.icon}</span>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{service.name}</h3>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-lg border font-semibold ${getStatusColor(service.status)}`}>
                      {service.status === "operational" ? "Operational" : service.status}
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500 mb-1">Uptime (30 days)</div>
                      <div className="text-lg font-bold text-green-600">{service.uptime}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500 mb-1">Avg Response Time</div>
                      <div className="text-lg font-bold text-blue-600">{service.responseTime}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500 mb-1">Last Incident</div>
                      <div className="text-sm text-gray-700">{service.lastIncident}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">⚡ Performance Metrics</h2>
              <p className="text-xl text-gray-600">
                Key performance indicators updated in real-time
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {performanceMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{metric.metric}</h3>
                    <span className={`text-sm px-3 py-1 rounded-full bg-${metric.color}-100 text-${metric.color}-800`}>
                      {metric.trend}
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">{metric.value}</div>
                  <div className="text-gray-600 text-sm">Last 24 hours average</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Incidents */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">📋 Recent Incidents</h2>
              <p className="text-xl text-gray-600">
                Transparency report of recent issues and their resolutions
              </p>
            </div>

            <div className="space-y-6">
              {recentIncidents.map((incident, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <span className="text-2xl mr-4 mt-1">{incident.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className="text-xl font-bold text-gray-900 mr-4">{incident.title}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(incident.severity)}`}>
                            {incident.severity}
                          </span>
                        </div>
                        <p className="text-gray-700 mb-4">{incident.description}</p>
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <span>📅 {incident.date}</span>
                          <span>⏱️ Duration: {incident.duration}</span>
                          <span className="text-green-600 font-medium">✅ {incident.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Maintenance */}
      <div className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">🔧 Scheduled Maintenance</h2>
              <p className="text-xl text-gray-600">
                Planned maintenance windows to keep our systems running smoothly
              </p>
            </div>

            <div className="space-y-6">
              {upcomingMaintenance.map((maintenance, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="text-2xl mr-4 mt-1">🔵</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">{maintenance.title}</h3>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          Scheduled
                        </span>
                      </div>
                      <p className="text-gray-700 mb-4">{maintenance.description}</p>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-500">Date & Time:</span>
                          <div className="text-gray-900">{maintenance.date} • {maintenance.time}</div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">Expected Downtime:</span>
                          <div className="text-gray-900">{maintenance.expectedDowntime}</div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <span className="font-medium text-gray-500">Impact:</span>
                        <div className="text-gray-700">{maintenance.impact}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Subscribe to Updates */}
      <div className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">🔔 Stay Updated</h2>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed">
              Get notified about system status changes, maintenance windows, 
              and important service updates.
            </p>
            
            <div className="bg-white bg-opacity-20 rounded-2xl p-8 backdrop-blur-sm mb-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <div className="text-2xl mb-2">📧</div>
                  <div className="font-bold mb-2">Email Alerts</div>
                  <div className="text-purple-100 text-sm">Instant notifications for incidents</div>
                </div>
                <div>
                  <div className="text-2xl mb-2">📱</div>
                  <div className="font-bold mb-2">SMS Updates</div>
                  <div className="text-purple-100 text-sm">Critical alerts via text message</div>
                </div>
                <div>
                  <div className="text-2xl mb-2">🔗</div>
                  <div className="font-bold mb-2">RSS Feed</div>
                  <div className="text-purple-100 text-sm">Subscribe to our status feed</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button className="bg-white text-purple-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-purple-50 transition duration-200 shadow-lg mx-2">
                🔔 Subscribe to Updates
              </button>
              <a
                href="/rss/status" 
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-purple-600 transition duration-200 mx-2"
              >
                📡 RSS Feed
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}